<template>
  <main class="wrapper-content">
    <LmFormHeader :title="$t('Passenger')" :name="form.fullname" />
    <template v-if="!isLoading">
      <b-card class="border">
        <b-row>
          <LmTextInput
            ref="firstNameInput"
            class="col-12 col-md-4"
            :label="$t('firstname')"
            v-model="$v.form.firstname.$model"
            :v="v('form.firstname')"
          />

          <LmTextInput
            class="col-12 col-md-4"
            :label="$t('lastname')"
            v-model="$v.form.lastname.$model"
            :v="v('form.lastname')"
          />

          <LmDocumentTypeNumber
            class="col-12 col-md-4"
            :value="form.documentTypeNumber"
            @change="onUpdateDocumentTypeNumber"
          />

          <LmEmailInput
            class="col-12 col-md-4"
            v-model="$v.form.email.$model"
            :v="v('form.email')"
            :label="$t('email')"
          />

          <LmNumberInputV2
            class="col-12 col-md-4"
            :label="$t('phone')"
            v-model="form.phone"
          />

          <LmDatePicker
            class="col-12 col-md-4"
            :showComponent="true"
            :config="{
              placeholder: dateFormat,
              dateFormat: dateFormat,
              allowInput: true,
            }"
            :maxDate="new Date()"
            label="Fecha de Nacimiento"
            v-model="form.birthdate"
          />

          <LmLanguageSelector
            class="col-12 col-md-4"
            :value="form.language"
            @change="(value) => { form.language = value }"
          />

          <LmNationalitySelector
            class="col-12 col-md-4"
            :value="form.nationality"
            @change="(value) => { form.nationality = value }"
          />

          <LmGenderSelector
            class="col-12 col-md-4"
            :value="form.gender"
            @change="(value) => { form.gender = value }"
          />
        </b-row>
      </b-card>

      <b-card v-if="programs?.length && isOnEditMode">
        <LmPassengerActivities
          :programs="programs"
          :programDetailFrontendRoute="programDetailFrontendRoute"
          :dailyActivitiesFrontendRoute="dailyActivitiesFrontendRoute"
          :servicesEditRoute="servicesEditRoute"
          :hotelsEditRoute="hotelsEditRoute"
          :leadersEditRoute="leadersEditRoute"
          :vehiclesEditRoute="vehiclesEditRoute"
        />
      </b-card>

      <div :class="isOnModal ? 'flex items-center gap-1' : 'buttons'">
        <LmButton
          v-if="isOnModal"
          buttonClass="btn-secondary"
          :value="$t('close')"
          @click="onClose"
          :isProcessing="isProcessing"
        />
        <LmButton
          v-else
          buttonClass="btn-secondary"
          :value="$t('go_back')"
          @click="onGoBack"
          :isProcessing="isProcessing"
        />

        <LmButton
          buttonClass="btn-primary"
          :value="$t('save')"
          @click="onSubmit"
          :isProcessing="isProcessing"
        />
        <LmButton
          v-if="isOnEditMode && !form.deleted"
          buttonClass="btn-danger"
          :value="$t('delete')"
          @click="onDeleteConfirmation"
          :isProcessing="isProcessing"
        />
        <LmButton
          v-if="isOnEditMode && form.deleted"
          value="Reactivar"
          buttonClass="btn-success"
          @click="onReactivateConfirmation"
          :isProcessing="isProcessing"
        />

      </div>
    </template>
    <b-row v-else>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col><b-skeleton type="input"></b-skeleton></b-col>
      <b-col md="12" class="my-4">
        <div>
          <div class="d-flex flex-wrap mb-3" style="gap: 1rem">
            <span
              class="p-2 border rounded-lg d-flex align-items-center justify-content-between"
              style="gap: 1rem"
              v-for="(item, index) in 4"
              :key="index"
            >
              <b-skeleton type="avatar"></b-skeleton>
              <b-skeleton width="100px"></b-skeleton>
            </span>
          </div>
          <b-skeleton type="button" width="150px"></b-skeleton>
        </div>
      </b-col>
    </b-row>
  </main>
</template>

<script>
// Components
import LmButton from '@/components/form/elements/LmButton'
import LmNumberInputV2 from '@/components/form/elements/LmNumberInputV2.vue'
import LmDatePicker from '@/components/form/elements/LmDatePicker'
import LmUploadFiles from '@/components/form/elements/LmUploadFiles'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmFileViewer from '@/components/LmFileViewer'
import LmEmailInput from '@/components/form/elements/LmEmailInput'
import LmLanguageSelector from '@/components/form/elements/LmLanguageSelector'
import LmNationalitySelector from '@/components/form/elements/LmNationalitySelector'
import LmPassengerActivities from './LmPassengerActivities.vue'
import LmGenderSelector from '@/components/form/elements/LmGenderSelector'
import LmDocumentTypeNumber from '@/components/form/elements/LmDocumentTypeNumber'
import LmTextInput from '@/components/form/elements/LmTextInput'

// Mixins
import lmStandardFormHelperMixin from '@/mixins/lmStandardFormHelperMixin'

// Utils
import { required, email } from 'vuelidate/lib/validators'
import commonsApi from '@/api/commons'
import moment from 'moment'

export default {
  mixins: [lmStandardFormHelperMixin],
  components: {
    LmUploadFiles,
    LmDatePicker,
    LmTextInput,
    LmNumberInputV2,
    LmTreeSelect,
    LmFileViewer,
    LmEmailInput,
    LmButton,
    LmLanguageSelector,
    LmNationalitySelector,
    LmPassengerActivities,
    LmDocumentTypeNumber,
    LmGenderSelector
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    isOnModal: {
      type: Boolean,
      default: false
    },
    dailyActivitiesFrontendRoute: {
      type: String
    },
    programDetailFrontendRoute: {
      type: String
    },
    frontendRouteToRedirectAfterCreated: {
      type: String
    },
    passengerActivitiesEndpoint: {
      type: String
    },
    servicesEditRoute: {
      type: String
    },
    hotelsEditRoute: {
      type: String
    },
    leadersEditRoute: {
      type: String
    },
    vehiclesEditRoute: {
      type: String
    },
    serviceEndpoint: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      dateFormat: 'DD-MM-YYYY',
      isLoading: false,
      form: {
        fullname: '',
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        language: {},
        nationality: {},
        gender: {},
        documentTypeNumber: { docType: {}, docNumber: '' },
        deleted: false,
        birthdate: null
      },
      programs: [],
      isProcessing: true
    }
  },
  validations: {
    form: {
      firstname: { required },
      lastname: { required },
      email: { email }
    }
  },
  methods: {
    onUpdateDocumentTypeNumber (value) {
      this.form.documentTypeNumber = value
    },
    async onSubmit () {
      try {
        await this.validateForm(this.$v)
        this.isProcessing = true
        this.form.issueDate = moment(this.form.issueDate, this.dateFormat).toDate()

        let res
        if (this.isOnEditMode) {
          await commonsApi.axiosPutWrapper(`/${this.serviceEndpoint}/${this.$route.params.id}`, this.form)
        } else {
          res = await commonsApi.axiosPostWrapper(`/${this.serviceEndpoint}`, this.form)
        }
        this.lmToastSuccessMessage(
          `${this.$t('saved_successfully')} '${this.form.lastname}, ${this.form.firstname}'`,
          `${this.$t('action_done_successfully')}`
        )
        this.hasChanged = false
        if (this.isOnModal) {
          this.$emit('itemCreated', {
            id: res.data._id,
            'passengers-firstname': res.data.firstname,
            'passengers-lastname': res.data.lastname
          })
          this.onClose()
        } else {
          this.onGoBack()
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },
    async loadData () {
      const res = await commonsApi.axiosGetWrapper(`/${this.serviceEndpoint}/${this.$route.params.id}`)
      this.form = res.data
      const programsResult = await commonsApi.axiosPostWrapper(this.passengerActivitiesEndpoint, {
        passengerId: this.$route.params.id
      })
      this.programs = programsResult.data.programs
    }
  }
}
</script>
